import { CHANNEL_ALIAS_TO_BACKEND, type ChannelAlias, fromBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import type {
  FeaturedHomeCategory,
  FeaturedHomeCategoryRaw,
  FeaturedHomeItem,
  FeaturedHomeItemRaw,
} from "../types/featured";

function normalizeChannel(value?: string | null): ChannelAlias | null {
  if (!value) return null;
  const upper = value.toUpperCase();
  try {
    return fromBackendChannel(upper);
  } catch {
    const lower = value.toLowerCase();
    if (lower in CHANNEL_ALIAS_TO_BACKEND) return lower as ChannelAlias;
    return null;
  }
}

function normalizePriceRubKopecks(raw: FeaturedHomeItemRaw): number | null {
  if (raw.price == null) return null;
  // Featured snapshots currently store decimal display price. Currency is
  // reserved for future locale/currency variants; RUB is the homepage display.
  return Math.round(raw.price * 100);
}

function normalizeItem(raw: FeaturedHomeItemRaw): FeaturedHomeItem {
  return {
    id: raw.id,
    pdpUrl: raw.pdpUrl,
    productId: raw.productId ?? null,
    sourcePlatform: raw.sourcePlatform ?? null,
    sourceProductKey: raw.sourceProductKey ?? null,
    channel: normalizeChannel(raw.sourcePlatform),
    title: raw.titleRu ?? "",
    imageUrl: raw.image ?? "",
    priceRubKopecks: normalizePriceRubKopecks(raw),
    currency: raw.currency ?? null,
    stockStatus: raw.stockStatus ?? null,
    position: raw.position ?? 0,
    snapshotRefreshedAt: raw.snapshotRefreshedAt ?? null,
  };
}

function normalizeCategory(raw: FeaturedHomeCategoryRaw): FeaturedHomeCategory {
  return {
    id: raw.id,
    categoryType: raw.categoryType,
    name: raw.nameRu ?? raw.categoryType,
    position: raw.position ?? 0,
    items: (raw.items ?? []).map(normalizeItem),
  };
}

export async function listFeaturedHome(ctx: ApiFetchContext): Promise<FeaturedHomeCategory[]> {
  const raw = await apiFetch<FeaturedHomeCategoryRaw[]>(ctx, "/featured/home/list");
  return (raw ?? []).map(normalizeCategory);
}
